// Pure helpers for the "Waiting on approval…" screen (Flow C, new device).
// Extracted so the countdown tick and status→next-action decision are
// unit-testable without a DOM, timers, or the network. See docs/AUTH-FLOWS.md.

// The waiting screen runs a 30-second window. Every ~1.5s it polls
// GET /auth/join/status and feeds the returned status + local countdown into
// nextAction() to decide what to do next.

// How long the new device waits for approval before giving up (seconds).
export const JOIN_WAIT_SECONDS = 30;

// After a terminal (denied / timeout) message, how long before auto-redirecting
// back to the login page (seconds).
export const JOIN_REDIRECT_SECONDS = 5;

// A next-action decision derived purely from (status, secondsLeft):
//  - 'wait'    : still awaiting approval and time remains — keep polling.
//  - 'login'   : approved — stop polling and complete /auth/join/finish.
//  - 'denied'  : trusted device rejected — show denied message, then redirect.
//  - 'timeout' : expired or the local countdown hit 0 — show timeout message,
//                then redirect.
export type JoinWaitAction = 'wait' | 'login' | 'denied' | 'timeout';

// nextAction maps a polled status string + the local seconds remaining to the
// single next action. Fail-safe: an unknown status while time remains keeps
// waiting; unknown status at/after 0 seconds is treated as timeout.
export function nextAction(status: string, secondsLeft: number): JoinWaitAction {
  switch (status) {
    case 'approved':
      return 'login';
    case 'denied':
      return 'denied';
    case 'expired':
      return 'timeout';
    case 'awaiting_approval':
    case 'pending':
      return secondsLeft > 0 ? 'wait' : 'timeout';
    default:
      // Unknown/garbled status: honour the local clock (fail closed at 0).
      return secondsLeft > 0 ? 'wait' : 'timeout';
  }
}

// tickCountdown returns the countdown value after one elapsed second, clamped at
// 0 (never negative). Pure so the per-second display update is testable.
export function tickCountdown(secondsLeft: number): number {
  const next = Math.floor(secondsLeft) - 1;
  return next < 0 ? 0 : next;
}

// The human-facing message shown for each terminal action before redirecting.
export function terminalMessage(action: JoinWaitAction): string {
  switch (action) {
    case 'denied':
      return 'Access was denied on your trusted device.';
    case 'timeout':
      return 'Access was not approved in time.';
    default:
      return '';
  }
}

// isTerminal reports whether an action ends the waiting loop (no more polling).
export function isTerminal(action: JoinWaitAction): boolean {
  return action !== 'wait';
}
